import { Fragment, useEffect, useMemo, useState } from 'react'
import { getPersistedItem, setPersistedItem } from 'utils/persistedStorage.ts'
import { useTranslation } from 'react-i18next'
import styles from './GenericFeedbackWidget.module.scss'
import { Banner, Button, Form, Heading, Modal } from '@boltenergy-be/design-system'
import { GenericFeedbackWidgetProps, FeedbackWidgetInputs, AvailableFeedbackSteps } from './types.ts'
import { FormProvider, useForm } from 'react-hook-form'
import FeedbackWidgetFormField from 'components/surveys/generic-feedback-widget/FeedbackWidgetFormField.tsx'

const GenericFeedbackWidget = <S extends AvailableFeedbackSteps>({
  id,
  steps,
  config,
  onClose,
  onCloseConfirmation,
  onSubmit,
  customCanPopUp,
  openFeedbackWidget,
  withIntro,
  introAs = 'widget'
}: Omit<GenericFeedbackWidgetProps<S>, 'as'>) => {
  // i18n
  const { t } = useTranslation(['feedbackWidget', 'common', 'validation'])

  // React Hook Form
  const hookForm = useForm<FeedbackWidgetInputs<AvailableFeedbackSteps>>()

  // Local state
  const [step, setStep] = useState<S>(steps[0])
  const [widgetState, setWidgetState] = useState<'intro' | 'form' | 'confirmation'>(withIntro && introAs === 'widget' ? 'form' : 'intro')
  const [showIntro, setShowIntro] = useState<boolean>(false)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  // Constants
  const activeStepIndex = steps.indexOf(step)
  const activeStep = steps[activeStepIndex]
  const isLastStep = activeStepIndex === steps.length - 1
  const canPopUp = typeof customCanPopUp === 'boolean' ? customCanPopUp : !getPersistedItem(id)
  const modalTitle = useMemo<string>(() => {
    switch (widgetState) {
      case 'intro': {
        return t(($) => $.salesFeedback.intro.title)
      }

      case 'form': {
        return `${steps.findIndex((s) => s === step) + 1} / ${steps.length}`
      }

      case 'confirmation': {
        return t(($) => $.salesFeedback.confirmation.title)
      }
    }
  }, [step, steps, t, widgetState])

  /**
   * Opens the widget from parent
   */
  useEffect(() => {
    if (withIntro && introAs === 'widget') {
      setShowIntro(openFeedbackWidget)
    } else {
      setModalOpen(openFeedbackWidget)
    }
  }, [withIntro, openFeedbackWidget, introAs])

  /**
   * Hide the widget for 30 days, close and reset it
   */
  const closeWidget = () => {
    if (typeof customCanPopUp !== 'boolean') {
      setPersistedItem(id, 'hidden', 30)
    }

    // close the widget
    setShowIntro(false)
    setModalOpen(false)
    hookForm.reset()
    setStep(steps[0])
    setWidgetState('form')
    setError(false)
    onClose?.()
  }

  /**
   * Handles the on submit
   */
  const submit = async (data: FeedbackWidgetInputs<AvailableFeedbackSteps>): Promise<void> => {
    setLoading(true)

    // Push entry to API
    try {
      if (isLastStep) {
        const { success } = await onSubmit(data)

        if (success) {
          setWidgetState('confirmation')
        } else {
          setError(true)
        }
      } else {
        setStep(steps[activeStepIndex + 1])
      }
    } catch (err) {
      const { message } = err as Error
      setError(true)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }

  if (!canPopUp) {
    return null
  }

  return (
    <>
      {withIntro && introAs === 'widget' && (
        <dialog className={styles['feedback-widget']} open={showIntro && !modalOpen}>
          <header>
            <Heading className={styles.title} as="h1" variant="h6">
              {t(($) => $.salesFeedback.intro.title)}
            </Heading>
            <Button size="small" variant="tertiary" leadingIcon="cross" type="button" onClick={closeWidget} className={styles.close} />
          </header>

          <p>{t(($) => $.salesFeedback.intro.description)}</p>

          <Button
            isFullwidth
            type="button"
            onClick={() => {
              setShowIntro(false)
              setModalOpen(true)
            }}
          >
            {t(($) => $.salesFeedback.intro.action)}
          </Button>
        </dialog>
      )}

      <Modal
        className={styles['feedback-modal']}
        isOpen={modalOpen}
        onRequestClose={() => (widgetState === 'confirmation' ? onCloseConfirmation() : closeWidget())}
        title={modalTitle}
        titleVariant="h4"
      >
        {widgetState === 'intro' && (
          <>
            <p>{t(($) => $.salesFeedback.intro.description)}</p>
            <Button
              type="button"
              isFullwidth
              size="large"
              onClick={() => {
                setWidgetState('form')
              }}
            >
              {t(($) => $.salesFeedback.intro.action)}
            </Button>
          </>
        )}

        {widgetState === 'form' && (
          <FormProvider {...hookForm}>
            <Form onSubmit={hookForm.handleSubmit(submit)} className={styles.form}>
              {steps.map(
                (step) =>
                  activeStep === step && (
                    <Fragment key={step}>
                      <FeedbackWidgetFormField key={step} active={activeStep === step} {...{ step }} {...config[step]} />

                      <Form.Footer className="flex-col gap-400">
                        <Button isFullwidth type="submit" size="large" disabled={error} {...{ loading }}>
                          {isLastStep ? t(($) => $.salesFeedback.confirmAction) : t(($) => $.next, { ns: 'common' })}
                        </Button>

                        {error && (
                          <Banner type="blocking">
                            {t(($) => $.formFeedback.error, {
                              ns: 'common'
                            })}
                          </Banner>
                        )}
                      </Form.Footer>
                    </Fragment>
                  )
              )}
            </Form>
          </FormProvider>
        )}

        {widgetState === 'confirmation' && (
          <>
            <p>{t(($) => $.salesFeedback.confirmation.description)}</p>
            <Button onClick={onCloseConfirmation} type="button" isFullwidth size="large">
              {t(($) => $.salesFeedback.confirmation.close)}
            </Button>
          </>
        )}
      </Modal>
    </>
  )
}

export default GenericFeedbackWidget
